import type { ResultOf } from '@graphql-typed-document-node/core'
import { GetCouponButton } from 'macaron-frontend-ui'
import { useCallback, useMemo, useState } from 'react'
import { useViewer } from '~/features/viewer/query'
import type { ViewerQuery } from '~/features/viewer/query'
import { useIkyuLogin } from '~/hooks/useIkyuLogin'
import { format, formatRageDate } from '~/lib/date/date'
import { dateTimeToDateText, format as formatWithDateTime } from '~/lib/date/dateTime'
import { fetcher } from '~/lib/graphql-request/swr'
import type { AcquireDiscountCouponError, Maybe } from '~/lib/graphql/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { css, sva } from '~/styled-system/css'
import { AcquireCoupon } from './AcquireCoupon.graphql'
import type { Coupon } from './Coupon.frag'
import { useInvalidateDiscountCoupons } from './query'
import type { DiscountCoupons } from './query'

export type CouponDetailListProps = {
  restaurantId: RestaurantId
  coupons: DiscountCoupons
  detailDirection?: 'horizontal' | 'vertical'
}

export function CouponDetailList({
  restaurantId,
  coupons,
  detailDirection = 'vertical',
}: CouponDetailListProps) {
  const { data: viewer, refetch } = useViewer()
  return coupons.map((c) => (
    <CouponDetail
      // 複数クーポンを全部獲得済みのユーザーが未ログインのときには全クーポンで「クーポンを獲得する」と表示されている。
      // このとき、ユーザーがクーポン獲得経由でログインすると、獲得操作したクーポン(optimistic update で更新される)以外は、
      // key=code では React がレンダリングをスキップして「クーポンを獲得する」のまま維持されてしまう。
      // そこで key には viewerAcquired も含めて「獲得済み」に表示が切り替わることを保証する。
      key={`${c.code}-${c.viewerAcquired}`}
      restaurantId={restaurantId}
      coupon={c}
      viewer={viewer}
      refetchViewer={refetch}
      detailDirection={detailDirection}
    />
  ))
}

type DetailProps = {
  restaurantId: RestaurantId
  coupon: DiscountCoupons[number]
  viewer?: ResultOf<typeof ViewerQuery>['viewer']
  refetchViewer: () => Promise<{}>
} & Pick<CouponDetailListProps, 'detailDirection'>

function couponErrorMessage(error?: Maybe<AcquireDiscountCouponError>): string | undefined {
  if (error === undefined || error === null) {
    return undefined
  }
  switch (error) {
    case 'ALREADY_ACQUIRED':
      return '' // 取得済のときはメッセージを出さずボタンの状態だけ変更する
    case 'LOGIN_REQUIRED':
      return '先にログインしてください'
    case 'NOT_AVAILABLE':
      return 'クーポンの利用ができませんでした'
    case 'NOT_FOUND':
      return 'クーポンが見つかりませんでした'
  }
}

const CouponDetail: React.FC<DetailProps> = ({
  restaurantId,
  coupon,
  viewer,
  refetchViewer,
  detailDirection,
}) => {
  const invalidateDiscountCoupons = useInvalidateDiscountCoupons()
  const viewerInfo = useMemo(() => viewer ?? { __typename: 'Guest' }, [viewer])

  // 会員の場合、クーポン獲得可能かを、ゲストの場合は一旦全て獲得可能に見せる
  const initialCanGetCoupon = viewerInfo.__typename === 'Member' ? !coupon.viewerAcquired : true
  const [canGetCoupon, setCanGetCoupon] = useState(initialCanGetCoupon)
  const [couponError, setCouponError] = useState<string | undefined>(undefined)

  const acquireCoupon = useCallback(async () => {
    try {
      setCanGetCoupon(false)
      const result = await fetcher({ query: AcquireCoupon, variables: { code: coupon.code } })
      const msg = couponErrorMessage(result.acquireDiscountCoupon)
      const failed = msg !== undefined && msg !== ''
      setCouponError(msg)
      // 再度押してもクーポンを得られないが、クーポン獲得済ではないのでボタンの状態を戻す(ボタンの文言は「クーポン獲得済み」)
      setCanGetCoupon(failed)
      if (!failed) {
        invalidateDiscountCoupons(restaurantId)
      }
    } catch {
      setCouponError('通信に失敗しました。時間をおいて再度お試しください。')
      setCanGetCoupon(true)
    }
  }, [coupon.code, invalidateDiscountCoupons, restaurantId])

  const afterLogin = useCallback(async () => {
    Promise.all([refetchViewer(), acquireCoupon()])
  }, [acquireCoupon, refetchViewer])

  const { containerRef, openLogin } = useIkyuLogin<HTMLDivElement>({ afterLogin })

  const handleClickButton = useCallback(async () => {
    if (!canGetCoupon || coupon.viewerAcquired) {
      return
    }
    if (viewerInfo.__typename === 'Member') {
      await acquireCoupon()
    } else {
      openLogin()
    }
  }, [acquireCoupon, canGetCoupon, coupon.viewerAcquired, openLogin, viewerInfo])

  const reserveDeadlineDate = format(
    dateTimeToDateText(coupon.reservablePeriod.to).dateText,
    'yyyy年M月d日(E)'
  )
  const reserveDeadlineTime = formatWithDateTime(coupon.reservablePeriod.to, 'H時')

  const visitableDateFrom =
    coupon.visitablePeriod.from && format(coupon.visitablePeriod.from, 'yyyy年M月d日(E)')
  const visitableDateTo =
    coupon.visitablePeriod.to && format(coupon.visitablePeriod.to, 'yyyy年M月d日(E)')

  const Card = detailDirection === 'horizontal' ? HorizontalCouponCard : VerticalCouponCard

  return (
    <div ref={containerRef}>
      {/* @ts-ignore */}
      <ikyu-login />
      <Card
        coupon={coupon}
        reserveDeadlineDate={reserveDeadlineDate}
        reserveDeadlineTime={reserveDeadlineTime}
        visitableDateFrom={visitableDateFrom}
        visitableDateTo={visitableDateTo}
        canGetCoupon={canGetCoupon}
        handleClickButton={handleClickButton}
        couponError={couponError}
      />
    </div>
  )
}

type CouponCardProps = {
  coupon: ResultOf<typeof Coupon>
  reserveDeadlineDate: string
  reserveDeadlineTime: string
  visitableDateFrom?: string | null
  visitableDateTo?: string | null
  canGetCoupon: boolean
  handleClickButton: () => void
  couponError?: string
}

const verticalCouponCardStyles = sva({
  slots: ['container', 'limited', 'heading', 'title', 'list', 'item'],
  base: {
    container: {
      spaceY: '4',
      rounded: 'md',
      border: '[1px solid]',
      borderColor: 'coupon.start',
      bg: 'white',
      p: '4',
    },
    limited: {
      fontSize: 'md',
      color: 'coupon.start',
      px: '5',
      py: '1',
      border: '[1px solid]',
      borderColor: 'coupon.start',
      rounded: 'full',
    },
    heading: {
      textAlign: 'center',
      color: 'coupon.start',
      spaceY: '1',
    },
    title: {
      fontSize: '5xl',
      fontWeight: 'bold',
    },
    list: {
      display: 'flex',
      columnGap: '2',
    },
    item: {
      fontWeight: 'bold',
      w: '[48px]',
      flexShrink: '0',
    },
  },
})

function VerticalCouponCard({
  coupon,
  reserveDeadlineDate,
  reserveDeadlineTime,
  visitableDateFrom,
  visitableDateTo,
  canGetCoupon,
  handleClickButton,
  couponError,
}: CouponCardProps) {
  const classes = verticalCouponCardStyles()
  return (
    <section className={classes.container}>
      <h2 className={classes.heading}>
        <span className={classes.limited}>先着{coupon.usableMemberCount}組限定</span>
        <p className={classes.title}>{money(coupon.discountAmount)}円OFFクーポン</p>
      </h2>
      <div className={css({ spaceY: '1', fontSize: 'sm' })}>
        {coupon.minimumReservationAmount && (
          <dl className={classes.list}>
            <dt className={classes.item}>利用条件</dt>
            <dd>総額{money(coupon.minimumReservationAmount)}円以上の予約で利用可</dd>
          </dl>
        )}
        <dl className={classes.list}>
          <dt className={classes.item}>予約期限</dt>
          <dd>
            {reserveDeadlineDate}
            {!reserveDeadlineTime.startsWith('23') && reserveDeadlineTime}まで
          </dd>
        </dl>
        {(visitableDateFrom || visitableDateTo) && (
          <dl className={classes.list}>
            <dt className={classes.item}>来店期限</dt>
            <dd>
              {formatRageDate({
                from: visitableDateFrom ?? undefined,
                to: visitableDateTo ?? undefined,
              })}
            </dd>
          </dl>
        )}
      </div>
      <GetCouponButton canGetCoupon={canGetCoupon} onClick={handleClickButton} />
      {couponError && (
        <div className={css({ color: 'error.text', textAlign: 'left' })}>{couponError}</div>
      )}
    </section>
  )
}

const horizontalCouponCardStyles = sva({
  slots: [
    'container',
    'detail',
    'button',
    'heading',
    'limited',
    'title',
    'conditions',
    'list',
    'item',
  ],
  base: {
    container: {
      width: '[600px]',
      borderRadius: 'md',
      border: '[1px solid]',
      borderColor: 'coupon.start',
      display: 'flex',
    },
    detail: {
      w: 'full',
      p: '4',
      borderRight: '[1px dotted]',
      borderColor: 'coupon.start',
      spaceY: '3',
    },
    button: {
      p: '8',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
      rowGap: '1',
      flexShrink: '0',
      width: '[240px]',
      textAlign: 'left',
    },
    heading: {
      textAlign: 'center',
      color: 'coupon.start',
      spaceY: '2',
    },
    limited: {
      fontSize: 'md',
      color: 'coupon.start',
      px: '5',
      py: '1',
      border: '[1px solid]',
      borderColor: 'coupon.start',
      rounded: 'full',
    },
    title: {
      fontSize: '5xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '3xl',
      },
    },
    conditions: {
      spaceY: '2',
      fontSize: 'sm',
      p: '4',
      bg: 'gray.100',
      rounded: 'sm',
    },
    list: {
      display: 'flex',
      columnGap: '2',
    },
    item: {
      w: '[80px]',
      fontWeight: 'bold',
      flexShrink: '0',
    },
  },
})

function HorizontalCouponCard({
  coupon,
  reserveDeadlineDate,
  reserveDeadlineTime,
  visitableDateFrom,
  visitableDateTo,
  canGetCoupon,
  handleClickButton,
  couponError,
}: CouponCardProps) {
  const classes = horizontalCouponCardStyles()
  return (
    <section className={classes.container}>
      <div className={classes.detail}>
        <h2 className={classes.heading}>
          <span className={classes.limited}>先着{coupon.usableMemberCount}組限定</span>
          <p className={classes.title}>{money(coupon.discountAmount)}円OFFクーポン</p>
        </h2>
        <div className={classes.conditions}>
          {coupon.minimumReservationAmount && (
            <dl className={classes.list}>
              <dt className={classes.item}>利用条件</dt>
              <dd>総額{money(coupon.minimumReservationAmount)}円以上の予約で利用可</dd>
            </dl>
          )}
          <dl className={classes.list}>
            <dt className={classes.item}>予約期限</dt>
            <dd>
              {reserveDeadlineDate}
              {!reserveDeadlineTime.startsWith('23') && reserveDeadlineTime}まで
            </dd>
          </dl>
          {(visitableDateFrom || visitableDateTo) && (
            <dl className={classes.list}>
              <dt className={classes.item}>来店期限</dt>
              <dd>
                {formatRageDate({
                  from: visitableDateFrom ?? undefined,
                  to: visitableDateTo ?? undefined,
                })}
              </dd>
            </dl>
          )}
        </div>
      </div>
      <div className={classes.button}>
        <GetCouponButton canGetCoupon={canGetCoupon} onClick={handleClickButton} />
        {couponError && (
          <p className={css({ color: 'error.text', textAlign: 'left' })}>{couponError}</p>
        )}
      </div>
    </section>
  )
}
